import type { DemoSubmission } from '@console/types'
import { useDemoSubmissions } from '../demo-submissions/queries.js'
import { Skeleton } from '../ui/index.js'
import styles from './DemoUsagePage.module.css'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * `LGH-049`, `PLT-105`. The founder's own direct instruction: real-time demo
 * usage tracking. Staff-only (`RequireAuth`, same guard `/onboarding-requests`
 * uses) — this is real pasted text from anonymous strangers, not public data.
 *
 * "Real-time" here is a query fresh on load/refresh, the same bar every other
 * list screen in this console already clears — `LGH-049`'s own text is
 * explicit that a websocket/push mechanism is a follow-on, not this task's
 * bar to clear.
 *
 * **Renders `labels` honestly.** A submission with no labels shows "No labels
 * yet." rather than omitting the section or implying a result that isn't
 * there — the task's own instruction.
 * ═══════════════════════════════════════════════════════════════════════════
 */

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString()
}

function SubmissionCard({ submission }: { submission: DemoSubmission }) {
  return (
    <li className={styles['card']}>
      <div className={styles['cardHeader']}>
        <p className={styles['visitor']}>{submission.visitorId}</p>
        <span className={styles['time']}>{formatWhen(submission.createdAt)}</span>
      </div>
      {submission.isRepeatVisitor && <span className={styles['repeatBadge']}>Repeat visitor</span>}
      <p className={styles['pastedText']}>{submission.pastedText}</p>

      {submission.labels.length === 0 ? (
        <p className={styles['noLabels']}>No labels yet.</p>
      ) : (
        <ul className={styles['labels']}>
          {submission.labels.map((label, index) => (
            <li key={index} className={styles['label']} data-disposition={label.disposition}>
              Line {label.lineIndex + 1} — {label.disposition} — {label.shownIdentity}
              {label.shownScore !== null && ` (score ${label.shownScore})`}
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

export function DemoUsagePage() {
  const submissions = useDemoSubmissions()

  return (
    <main className={styles['page']}>
      <div className={styles['header']}>
        <h1>Demo usage</h1>
      </div>

      {submissions.isPending && (
        <div className={styles['list']}>
          <Skeleton height="6rem" />
          <Skeleton height="6rem" />
          <Skeleton height="6rem" />
        </div>
      )}
      {submissions.isError && (
        <p role="alert" style={{ color: 'var(--stop)' }}>
          Demo usage could not be loaded.
        </p>
      )}
      {submissions.data !== undefined && (
        <ul className={styles['list']}>
          {submissions.data.length === 0 && (
            <p className={styles['empty']}>No demo submissions yet.</p>
          )}
          {submissions.data.map((submission) => (
            <SubmissionCard key={submission.id} submission={submission} />
          ))}
        </ul>
      )}
    </main>
  )
}
